import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import FormError from "../../../../Components/v2/Form/FormError";
import Table from "../../../../Components/v2/Table";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import Pagination from "../../../../Components/v2/Pagination";
import ProductSummarySkeleton from "../../../../Components/v2/Accounting/ProductSummarySkeleton";
import {
  useBusinessProductDispatch,
  useBusinessProductState,
  requestListInventoryLedger,
  requestGetBusinessProduct,
  clearInventoryLedger,
} from "../../../../Context";
import { cancelAPIRequest, formatDateTime } from "../../../../Utils";

// Helper function to get transaction type display text
const getTransactionTypeText = (type) => {
  const typeMap = {
    PURCHASE: "products.stockHistory.purchase",
    SALE: "products.stockHistory.sale",
    ADJUSTMENT_ADD: "products.stockHistory.adjustmentAdd",
    ADJUSTMENT_REMOVE: "products.stockHistory.adjustmentRemove",
    RETURN_IN: "products.stockHistory.returnIn",
    RETURN_OUT: "products.stockHistory.returnOut",
    TRANSFER_IN: "products.stockHistory.transferIn",
    TRANSFER_OUT: "products.stockHistory.transferOut",
    INITIAL: "products.stockHistory.initial",
    PRODUCTION: "products.stockHistory.production",
    COMPONENT_USAGE: "products.stockHistory.componentUsage",
    REVERSAL: "products.stockHistory.reversal",
    WRITE_DOWN: "products.stockHistory.writeDown",
  };
  return typeMap[type] || type;
};

// Helper function to get source type display text
const getSourceTypeText = (type) => {
  const typeMap = {
    BUSINESS_TRANSACTION: "products.stockHistory.businessTransaction",
    ACCOUNTING_TRANSACTION: "products.stockHistory.accountingTransaction",
    MANUAL_ADJUSTMENT: "products.stockHistory.manualAdjustment",
    INITIAL_SETUP: "products.stockHistory.initialSetup",
    PRODUCTION: "products.stockHistory.production",
  };
  return typeMap[type] || type;
};

// Helper function to get transaction type badge color
const getTransactionTypeBadgeColor = (type) => {
  const colorMap = {
    PURCHASE: "bg-green-100 text-green-800",
    SALE: "bg-red-100 text-red-800",
    ADJUSTMENT_ADD: "bg-blue-100 text-blue-800",
    ADJUSTMENT_REMOVE: "bg-yellow-100 text-yellow-800",
    RETURN_IN: "bg-indigo-100 text-indigo-800",
    RETURN_OUT: "bg-purple-100 text-purple-800",
    TRANSFER_IN: "bg-teal-100 text-teal-800",
    TRANSFER_OUT: "bg-orange-100 text-orange-800",
    INITIAL: "bg-gray-100 text-gray-800",
    PRODUCTION: "bg-pink-100 text-pink-800",
    COMPONENT_USAGE: "bg-cyan-100 text-cyan-800",
    REVERSAL: "bg-gray-100 text-gray-800",
    WRITE_DOWN: "bg-rose-100 text-rose-800",
  };
  return colorMap[type] || "bg-gray-100 text-gray-800";
};

const ProductStockHistory = () => {
  const { t, i18n } = useTranslation();
  const { productId } = useParams();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  const dispatch = useBusinessProductDispatch();
  const {
    request,
    businessProduct: product,
    businessProductLoading: productLoading,
    businessProductError: productError,
    inventoryLedger,
    inventoryLedgerLoading: ledgerLoading,
    inventoryLedgerError: ledgerError,
    inventoryLedgerNextTokens,
  } = useBusinessProductState();

  const loading = productLoading || ledgerLoading;

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearInventoryLedger(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load product and inventory ledger data
  useEffect(() => {
    if (productId) {
      // Fetch product details
      requestGetBusinessProduct(dispatch, { id: productId });

      // Fetch inventory ledger data
      requestListInventoryLedger(dispatch, {
        productID: productId,
        page: 1,
        limit: perPage,
      });
    }
  }, [dispatch, productId, perPage]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (inventoryLedgerNextTokens[selectedPage] || selectedPage === 1) {
      requestListInventoryLedger(dispatch, {
        productID: productId,
        page: selectedPage,
        limit: perPage,
        nextTokens: inventoryLedgerNextTokens[selectedPage],
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
    setPage(1);
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Page header */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {t("products.stockHistory.label")}
              </h2>
            </div>
          </div>
        </div>

        {/* Error States */}
        {productError && (
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <FormError
              title="common.errors.dataTitle"
              error={productError}
              onRetry={() =>
                requestGetBusinessProduct(dispatch, { id: productId })
              }
            />
          </div>
        )}

        {/* Product Summary */}
        {loading ? (
          <ProductSummarySkeleton />
        ) : (
          product &&
          !productError && (
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="mb-4 md:mb-0">
                  <h3 className="text-sm font-medium text-gray-500">
                    {t("products.label_one")}
                  </h3>
                  <p className="text-lg font-semibold text-gray-900 truncate max-w-md">
                    {product.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {product.type === "GOODS"
                      ? t("products.types.goods")
                      : t("products.types.services")}
                  </p>
                </div>

                <div className="flex flex-row space-x-6">
                  <div className="text-center flex flex-col justify-end">
                    <span className="text-3xl font-bold text-gray-900">
                      {product.currentStock || 0}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      {t("products.stockHistory.current")}
                    </p>
                  </div>

                  <div className="text-center flex flex-col justify-end min-h-[60px]">
                    <span className="text-sm font-medium text-gray-900">
                      {formatDateTime(
                        product.stockLastUpdatedAt,
                        i18n.language,
                      )}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      {t("common.lastUpdated")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {/* Inventory Ledger Table */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <Table
            isLoading={loading}
            error={ledgerError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "products.fields.date",
                accessor: (item) => <DateTimeCell value={item.createdAt} />,
              },
              {
                header: "products.fields.type",
                accessor: (item) => (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeBadgeColor(
                      item.type,
                    )}`}
                  >
                    {t(getTransactionTypeText(item.type))}
                  </span>
                ),
              },
              {
                header: "products.fields.quantity",
                accessor: (item) => {
                  const isNegative = [
                    "SALE",
                    "ADJUSTMENT_REMOVE",
                    "RETURN_OUT",
                    "TRANSFER_OUT",
                    "COMPONENT_USAGE",
                  ].includes(item.type);
                  return (
                    <span
                      className={isNegative ? "text-red-600" : "text-green-600"}
                    >
                      {isNegative ? "-" : "+"}
                      {item.quantity}
                    </span>
                  );
                },
                align: "right",
              },
              {
                header: "products.fields.before",
                accessor: (item) =>
                  item.quantityBefore !== undefined ? item.quantityBefore : "-",
                align: "right",
              },
              {
                header: "products.fields.after",
                accessor: (item) =>
                  item.quantityAfter !== undefined ? item.quantityAfter : "-",
                align: "right",
              },
              {
                header: "products.fields.source",
                accessor: (item) => (
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {t(getSourceTypeText(item.sourceType))}
                    </div>
                    {item.reference && (
                      <div className="text-sm text-gray-500">
                        {item.reference}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                header: "products.fields.note",
                accessor: (item) => item.note || "-",
              },
            ]}
            data={inventoryLedger}
          />
        </div>

        <Pagination
          nextTokens={inventoryLedgerNextTokens}
          currentPage={page}
          isLoading={loading}
          onPageChange={onPageChange}
          perPage={perPage}
          onPerPageChange={onPerPageChange}
        />
      </main>
    </AccountingWrapper>
  );
};

export default ProductStockHistory;
